import { BookmarkAdd, BookmarkAdded, Cancel, Save } from '@mui/icons-material';
import { IconButton, Menu, TextField, Tooltip } from '@mui/material';
import { useEnter, useEscape, useKeyPresses } from '@qfa/react-components';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBookmarks } from 'kineticUtils/hooks/useBookmarks';
import { useContextMenu } from 'kineticUtils/hooks/useContextMenu';

import usePageHeaderStyles from '../styles';
import { BookmarkButtonProps } from './types';

const BookmarkButton: React.FunctionComponent<BookmarkButtonProps> = (props) => {
  const { title } = props;

  const { t } = useTranslation(['app', 'common']);
  const classes = usePageHeaderStyles(props);
  const { props: contextMenuProps, handleOpenMenu, handleCloseMenu } = useContextMenu();
  const { bookmarkPage, unbookmarkPage, isBookmarked, loading } = useBookmarks({
    onSave: handleCloseMenu,
  });
  const loadBookMarkTimer = useRef<ReturnType<typeof setTimeout>>();

  const [bookmarkTitle, setBookmarkTitle] = useState(title);

  useEffect(() => {
    setBookmarkTitle(title);
  }, [title]);

  useEffect(() => {
    return () => {
      if (loadBookMarkTimer.current) {
        clearTimeout(loadBookMarkTimer.current);
      }
    };
  });

  const saveBookmark = () => {
    bookmarkPage(bookmarkTitle);
    setBookmarkTitle(title);
  };

  const onKeyUp = useKeyPresses(
    useEnter({ callback: saveBookmark }),
    useEscape({ callback: handleCloseMenu })
  );

  const titleField = useRef<HTMLInputElement>(null);

  const renderUnbookmark = () => {
    return (
      <Tooltip title={t('removeBookmark')}>
        <IconButton size='small' style={{ fontSize: 'inherit' }} onClick={unbookmarkPage}>
          <BookmarkAdded fontSize='small' color='primary' />
        </IconButton>
      </Tooltip>
    );
  };

  const renderNameField = () => {
    return (
      <Menu {...contextMenuProps} MenuListProps={{ style: { padding: 0 } }}>
        <div className={classes.bookmarkName}>
          <TextField
            label={t('bookmarkTitle')}
            margin='dense'
            onKeyUp={onKeyUp}
            inputRef={titleField}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBookmarkTitle(e.target.value)}
            value={bookmarkTitle}
            size='small'
          />
          <span className={classes.addons}>
            <Tooltip title={t('common:save')}>
              <IconButton
                size='small'
                disabled={!bookmarkTitle.trim().length}
                onClick={saveBookmark}
              >
                <Save fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common:cancel')}>
              <IconButton size='small' onClick={handleCloseMenu}>
                <Cancel fontSize='small' />
              </IconButton>
            </Tooltip>
          </span>
        </div>
      </Menu>
    );
  };

  const renderBookmark = () => {
    return (
      <>
        <Tooltip title={t('addBookmark')}>
          <IconButton
            size='small'
            style={{ fontSize: 'inherit' }}
            onClick={(e) => {
              handleOpenMenu(e);
              loadBookMarkTimer.current = setTimeout(() => {
                titleField.current?.focus();
              }, 0);
            }}
          >
            <BookmarkAdd fontSize='small' />
          </IconButton>
        </Tooltip>
        {renderNameField()}
      </>
    );
  };

  const renderBookmarkButton = () => {
    if (loading) {
      return null;
    }
    return isBookmarked ? renderUnbookmark() : renderBookmark();
  };

  return renderBookmarkButton();
};

export default BookmarkButton;
